/**
 * "My mailbox" — the address whose threads `/mail` opens on, per person, across reloads.
 *
 * Enquiries arrive in one shared mailbox with the responsible purchaser on Cc, so each purchaser
 * filters the list to the threads they are on. The address used to live in `SessionProvider`'s
 * React state and reset on every reload (Akhil, 2026-09-18: "make sure the field retains the
 * saved change"). Same mechanism as the operating company (`company.ts`): module state that any
 * component can subscribe to, restored from the Clerk user's `unsafeMetadata` by
 * `ClerkIdentityBridge` once the user loads, and written back there on every change. No browser
 * storage — the metadata is per person and server-side.
 *
 * `''` is a real value: "show everything". It persists like any other, so clearing the filter
 * also survives a reload.
 */
import { useSyncExternalStore } from 'react'

type Persister = (address: string) => void
let current = ''
let persister: Persister | null = null
const listeners = new Set<() => void>()

const normalise = (value: unknown) => (typeof value === 'string' ? value.trim().toLowerCase() : '')

export function registerMailboxPersister(fn: Persister | null): void {
  persister = fn
}

export function subscribeMailbox(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getMyMailbox(): string {
  return current
}

export function useMyMailbox(): string {
  return useSyncExternalStore(subscribeMailbox, getMyMailbox, getMyMailbox)
}

/** Install a value restored FROM the persister — notifies, never re-persists. */
export function restoreMyMailbox(value: unknown): void {
  const next = normalise(value)
  if (next === current) return
  current = next
  listeners.forEach((l) => l())
}

export function setMyMailbox(value: unknown): void {
  const next = normalise(value)
  if (next === current) return
  current = next
  persister?.(next)
  listeners.forEach((l) => l())
}

/** Tests only — module state persists across cases otherwise. */
export function __resetMailboxForTests(): void {
  current = ''
  persister = null
  listeners.clear()
}
